const UNSAFE_ELEMENTS = 'script, iframe, frame, frameset, object, embed, form, input, button, textarea, select, option, template, noscript, base'
const URL_ATTRIBUTES = new Set(['href', 'src', 'poster', 'xlink:href', 'action', 'formaction'])

const normaliseHtmlBoundary = (value: string) => value.trim().replace(/\\(?=<\/?html\b)/gi, '')

const safeUrl = (value: string, attribute: string) => {
    const url = value.trim()
    if (!url || url.startsWith('#')) return true
    if (/^(?:https?:|mailto:|tel:)/i.test(url)) return true
    return (attribute === 'src' || attribute === 'poster') && /^data:image\/(?:png|jpe?g|gif|webp|svg\+xml);/i.test(url)
}

const cleanCss = (value: string) => value
    .replace(/@import\s+(?:url\()?[^;]+;?/gi, '')
    .replace(/expression\s*\([^)]*\)/gi, '')
    .replace(/url\s*\(\s*(['"]?)\s*javascript:[^)]*\)/gi, 'none')

const parseAndSanitize = (reportHtml: string, title: string) => {
    const parser = new DOMParser()
    const parsed = parser.parseFromString(normaliseHtmlBoundary(reportHtml), 'text/html')

    parsed.querySelectorAll(UNSAFE_ELEMENTS).forEach((element) => element.remove())
    parsed.querySelectorAll('meta[http-equiv]').forEach((element) => {
        if (element.getAttribute('http-equiv')?.toLowerCase() === 'refresh') element.remove()
    })
    parsed.querySelectorAll('style').forEach((element) => {
        element.textContent = cleanCss(element.textContent || '')
    })
    parsed.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            const name = attribute.name.toLowerCase()
            if (name.startsWith('on') || name === 'srcdoc') {
                element.removeAttribute(attribute.name)
                return
            }
            if (name === 'style') {
                element.setAttribute(attribute.name, cleanCss(attribute.value))
                return
            }
            if (URL_ATTRIBUTES.has(name) && !safeUrl(attribute.value, name)) element.removeAttribute(attribute.name)
        })
        if (element.tagName === 'A' && element.getAttribute('href')) {
            element.setAttribute('target', '_blank')
            element.setAttribute('rel', 'noopener noreferrer')
        }
    })

    parsed.documentElement.setAttribute('lang', parsed.documentElement.getAttribute('lang') || 'en')
    const meta = parsed.createElement('meta')
    meta.setAttribute('charset', 'utf-8')
    parsed.head.insertBefore(meta, parsed.head.firstChild)
    if (!parsed.title) parsed.title = title

    // These are fallbacks only: inserting them before the supplied styles lets the report's own
    // CSS and inline formatting remain authoritative in the preview and both exports.
    const defaults = parsed.createElement('style')
    defaults.setAttribute('data-enhanced-report-defaults', '')
    defaults.textContent = `
        html { background: #fff; color: #0f172a; }
        body { box-sizing: border-box; margin: 0 auto; padding: 48px; max-width: 960px; background: #fff; color: #0f172a; font-family: Arial, Helvetica, sans-serif; line-height: 1.5; overflow-wrap: anywhere; }
        *, *::before, *::after { box-sizing: border-box; }
        img, svg { max-width: 100%; height: auto; }
        table { max-width: 100%; border-collapse: collapse; }
        h1, h2, h3, h4, h5, h6 { break-after: avoid-page; page-break-after: avoid; mso-keep-with-next: yes; }
        p, li { orphans: 3; widows: 3; mso-pagination: widow-orphan; }
        tr, figure, img, svg, canvas, pre, blockquote { break-inside: avoid-page; page-break-inside: avoid; }
        @page { size: A4; margin: 16mm; }
    `
    parsed.head.insertBefore(defaults, meta.nextSibling)
    return parsed
}

export const prepareEnhancedNarrativeHtml = (reportHtml: string, title: string) => {
    const parsed = parseAndSanitize(reportHtml, title)
    return `<!doctype html>\n${parsed.documentElement.outerHTML}`
}

export const enhancedNarrativePlainText = (reportHtml: string) =>
    parseAndSanitize(reportHtml, 'Enhanced report').body.textContent?.replace(/\n{3,}/g, '\n\n').trim() || ''
